import React, { useEffect, useState } from 'react';
import { Plus, Shield, Building2, Users as UsersIcon, Trash2, Ban, CheckCircle, Edit2, X, Loader2, RefreshCw, MapPin } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { usersApi, teamsApi, rolesApi, UserRecord, TeamRecord, RoleRecord } from '../services/creatorApi';
import { toast } from 'sonner';
import { ConfirmModal } from '../components/ConfirmModal';
export function SuperAdminSettings() {
  const {
    currentUser,
    workspaces,
    roles,
    refreshTeams
  } = useWorkspace();
  const [allUsers, setAllUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'users' | 'workspaces'>('users');
  const [selectedTeamFilter, setSelectedTeamFilter] = useState<string>('all');
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [userToDelete, setUserToDelete] = useState<UserRecord | null>(null);
  // Create Team Modal state
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamRegions, setNewTeamRegions] = useState<string[]>([]);
  const [newRegionInput, setNewRegionInput] = useState('');
  const [newTeamLogoUrl, setNewTeamLogoUrl] = useState('');
  const [newTeamPrimaryColor, setNewTeamPrimaryColor] = useState('#2563eb');
  // Security check
  if (currentUser.Role !== 'Super Admin') {
    return <div className="p-8 text-center text-red-600" data-id="element-6572">
        Access Denied: Super Admin privileges required.
      </div>;
  }
  const loadUsers = async () => {
    setLoading(true);
    try {
      const users = await usersApi.list();
      setAllUsers(users);
    } catch (err) {
      console.error('Failed to load users:', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadUsers();
  }, []);
  const filteredUsers = selectedTeamFilter === 'all' ? allUsers : allUsers.filter(u => u.Team === selectedTeamFilter);
  const handleToggleStatus = async (user: UserRecord) => {
    const newStatus = user.Status === 'Active' ? 'Suspended' : 'Active';
    try {
      await usersApi.update(user.ID, {
        Status: newStatus
      });
      toast.success(`User ${newStatus === 'Active' ? 'activated' : 'suspended'}`);
      loadUsers();
    } catch (err) {
      toast.error('Failed to update user status');
    }
  };
  const handleDeleteUser = (user: UserRecord) => {
    setUserToDelete(user);
  };
  const confirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await usersApi.delete(userToDelete.ID);
      toast.success('User deleted');
      loadUsers();
    } catch (err) {
      toast.error('Failed to delete user');
    }
    setUserToDelete(null);
  };
  const handleStartEditRole = (userId: string, currentRole: string) => {
    setEditingUserId(userId);
    setSelectedRole(currentRole);
  };
  const handleSaveRole = async (userId: string) => {
    try {
      await usersApi.update(userId, {
        Role: selectedRole as any
      });
      toast.success('Role updated');
      setEditingUserId(null);
      loadUsers();
    } catch (err) {
      toast.error('Failed to update role');
    }
  };
  const handleAddRegion = (e: React.FormEvent) => {
    e.preventDefault();
    if (newRegionInput.trim() && !newTeamRegions.includes(newRegionInput.trim())) {
      setNewTeamRegions([...newTeamRegions, newRegionInput.trim()]);
      setNewRegionInput('');
    }
  };
  const handleRemoveRegion = (region: string) => {
    setNewTeamRegions(newTeamRegions.filter(r => r !== region));
  };
  const resetCreateTeamForm = () => {
    setNewTeamName('');
    setNewTeamRegions([]);
    setNewRegionInput('');
    setNewTeamLogoUrl('');
    setNewTeamPrimaryColor('#2563eb');
  };
  const handleCreateTeam = () => {
    setShowCreateTeamModal(true);
  };
  const handleSubmitCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTeamName.trim()) {
      toast.error('Team name is required');
      return;
    }
    setIsCreatingTeam(true);
    try {
      const regionRows = newTeamRegions.map(r => ({
        Region_Name: r
      }));
      await teamsApi.create({
        Team_Name: newTeamName.trim(),
        Team_ID: teamsApi.generateTeamId(newTeamName.trim()),
        Logo_Url: newTeamLogoUrl.trim() || undefined,
        Primary_Color: newTeamPrimaryColor || undefined,
        ...(regionRows.length > 0 ? {
          Team_Regions: regionRows
        } : {})
      });
      toast.success('Team created successfully');
      setShowCreateTeamModal(false);
      resetCreateTeamForm();
      refreshTeams();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to create team', {
        description: msg
      });
    } finally {
      setIsCreatingTeam(false);
    }
  };
  const getRoleBadgeColor = (roleName: string) => {
    switch (roleName) {
      case 'Super Admin':
        return 'bg-purple-100 text-purple-800';
      case 'Team Manager':
        return 'bg-blue-100 text-blue-800';
      case 'Operator':
        return 'bg-green-100 text-green-800';
      case 'Viewer':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const activeUsers = allUsers.filter(u => u.Status === 'Active');
  return <div className="space-y-6" data-id="element-6573">
      <div className="flex items-center justify-between" data-id="element-6574">
        <div data-id="element-6575">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-6576">
            Super Admin Settings
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-6577">
            Global management of users and teams across all workspaces.
          </p>
        </div>
        <button onClick={loadUsers} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Refresh" data-id="element-6578">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-6579" />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4" data-id="element-6580">
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-6581">
          <div className="flex items-center gap-3" data-id="element-6582">
            <div className="p-2 bg-blue-50 rounded-lg" data-id="element-6583">
              <UsersIcon className="h-5 w-5 text-blue-600" data-id="element-6584" />
            </div>
            <div data-id="element-6585">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-6586">
                {allUsers.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-6587">Total Users</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-6588">
          <div className="flex items-center gap-3" data-id="element-6589">
            <div className="p-2 bg-green-50 rounded-lg" data-id="element-6590">
              <Building2 className="h-5 w-5 text-green-600" data-id="element-6591" />
            </div>
            <div data-id="element-6592">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-6593">
                {workspaces.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-6594">Teams</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-6595">
          <div className="flex items-center gap-3" data-id="element-6596">
            <div className="p-2 bg-purple-50 rounded-lg" data-id="element-6597">
              <Shield className="h-5 w-5 text-purple-600" data-id="element-6598" />
            </div>
            <div data-id="element-6599">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-6600">
                {roles.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-6601">Roles</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-6602">
          <div className="flex items-center gap-3" data-id="element-6603">
            <div className="p-2 bg-yellow-50 rounded-lg" data-id="element-6604">
              <UsersIcon className="h-5 w-5 text-yellow-600" data-id="element-6605" />
            </div>
            <div data-id="element-6606">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-6607">
                {activeUsers.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-6608">Active Users</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-6609">
        <nav className="-mb-px flex space-x-8" data-id="element-6610">
          <button onClick={() => setActiveTab('users')} className={`${activeTab === 'users' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`} data-id="element-6611">
            <UsersIcon className="h-4 w-4" data-id="element-6612" />
            All Users ({allUsers.length})
          </button>
          <button onClick={() => setActiveTab('workspaces')} className={`${activeTab === 'workspaces' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`} data-id="element-6613">
            <Building2 className="h-4 w-4" data-id="element-6614" />
            Teams ({workspaces.length})
          </button>
        </nav>
      </div>

      {activeTab === 'users' && <div className="space-y-4" data-id="element-6615">
          <div className="flex justify-between items-center" data-id="element-6616">
            <div className="flex items-center gap-2" data-id="element-6617">
              <span className="text-sm text-gray-500" data-id="element-6618">Filter by Team:</span>
              <select className="rounded-md border-gray-300 text-sm focus:ring-blue-500 focus:border-blue-500" value={selectedTeamFilter} onChange={e => setSelectedTeamFilter(e.target.value)} data-id="element-6619">
                <option value="all" data-id="element-6620">All Teams ({allUsers.length} users)</option>
                {workspaces.map(ws => <option key={ws.id} value={ws.name} data-id="element-6621">
                    {ws.name} (
                    {allUsers.filter(u => u.Team === ws.name).length} users)
                  </option>)}
              </select>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-6622">
            {loading ? <div className="p-12 text-center" data-id="element-6623">
                <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-6624" />
                <p className="mt-2 text-sm text-gray-500" data-id="element-6625">Loading users...</p>
              </div> : filteredUsers.length === 0 ? <div className="p-12 text-center" data-id="element-6626">
                <UsersIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-6627" />
                <p className="mt-2 text-sm text-gray-500" data-id="element-6628">No users found</p>
              </div> : <table className="min-w-full divide-y divide-gray-200" data-id="element-6629">
                <thead className="bg-gray-50" data-id="element-6630">
                  <tr data-id="element-6631">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6632">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6633">
                      Team
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6634">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6635">
                      Regions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6636">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6637">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-6638">
                  {filteredUsers.map(user => <tr key={user.ID} className="hover:bg-gray-50" data-id="element-6639">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6640">
                        <div className="flex items-center" data-id="element-6641">
                          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-xs font-medium text-white mr-3" data-id="element-6642">
                            {user.User_Name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div data-id="element-6643">
                            <div className="text-sm font-medium text-gray-900" data-id="element-6644">
                              {user.User_Name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-6645">
                              {user.Email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6646">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-6647">
                          {user.Team}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6648">
                        {editingUserId === user.ID ? <div className="flex items-center gap-2" data-id="element-6649">
                            <select className="rounded-md border-gray-300 text-sm" value={selectedRole} onChange={e => setSelectedRole(e.target.value)} data-id="element-6650">
                              <option value="Super Admin" data-id="element-6651">Super Admin</option>
                              <option value="Team Manager" data-id="element-6652">Team Manager</option>
                              <option value="Operator" data-id="element-6653">Operator</option>
                              <option value="Viewer" data-id="element-6654">Viewer</option>
                            </select>
                            <button onClick={() => handleSaveRole(user.ID)} className="p-1 text-green-600 hover:text-green-800" data-id="element-6655">
                              <CheckCircle className="h-4 w-4" data-id="element-6656" />
                            </button>
                            <button onClick={() => setEditingUserId(null)} className="p-1 text-gray-400 hover:text-gray-600" data-id="element-6657">
                              <X className="h-4 w-4" data-id="element-6658" />
                            </button>
                          </div> : <div className="flex items-center gap-2" data-id="element-6659">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(user.Role)}`} data-id="element-6660">
                              {user.Role}
                            </span>
                            {user.ID !== currentUser.ID && <button onClick={() => handleStartEditRole(user.ID, user.Role)} className="p-1 text-gray-400 hover:text-blue-600" title="Change Role" data-id="element-6661">
                                <Edit2 className="h-3 w-3" data-id="element-6662" />
                              </button>}
                          </div>}
                      </td>
                      <td className="px-6 py-4" data-id="element-6663">
                        <div className="flex flex-wrap gap-1 max-w-[150px]" data-id="element-6664">
                          {user.User_Regions && user.User_Regions.length > 0 ? user.User_Regions.map((r, i) => <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-700" data-id="element-6665">
                                {r.Region_Name}
                              </span>) : <span className="text-xs text-gray-400 italic" data-id="element-6666">
                              All
                            </span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6667">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${user.Status === 'Active' ? 'bg-green-100 text-green-800' : user.Status === 'Suspended' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-6668">
                          {user.Status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-6669">
                        {user.ID !== currentUser.ID && <div className="flex items-center justify-end gap-2" data-id="element-6670">
                            {user.Status === 'Active' ? <button onClick={() => handleToggleStatus(user)} className="text-yellow-600 hover:text-yellow-900" title="Suspend" data-id="element-6671">
                                <Ban className="h-4 w-4" data-id="element-6672" />
                              </button> : <button onClick={() => handleToggleStatus(user)} className="text-green-600 hover:text-green-900" title="Activate" data-id="element-6673">
                                <CheckCircle className="h-4 w-4" data-id="element-6674" />
                              </button>}
                            <button onClick={() => handleDeleteUser(user)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-6675">
                              <Trash2 className="h-4 w-4" data-id="element-6676" />
                            </button>
                          </div>}
                      </td>
                    </tr>)}
                </tbody>
              </table>}
          </div>
        </div>}

      {activeTab === 'workspaces' && <div className="space-y-4" data-id="element-6677">
          <div className="flex justify-end" data-id="element-6678">
            <button onClick={handleCreateTeam} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-6679">
              <Plus className="h-4 w-4" data-id="element-6680" />
              Create Team
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-6681">
            {workspaces.map(ws => <div key={ws.id} className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-6682">
                <div className="flex items-start justify-between mb-4" data-id="element-6683">
                  <div className="p-2 bg-blue-50 rounded-lg" data-id="element-6684">
                    <Building2 className="h-6 w-6 text-blue-600" data-id="element-6685" />
                  </div>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-6686">
                  {ws.name}
                </h3>
                <p className="text-sm text-gray-500 mb-4" data-id="element-6687">ID: {ws.id}</p>
                <div className="mt-4 space-y-2" data-id="element-6688">
                  <div className="flex justify-between text-sm" data-id="element-6689">
                    <span className="text-gray-500" data-id="element-6690">Users</span>
                    <span className="font-medium text-gray-900" data-id="element-6691">
                      {allUsers.filter(u => u.Team === ws.name).length}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-6692">
                    <span className="text-gray-500" data-id="element-6693">Regions</span>
                    <span className="font-medium text-gray-900" data-id="element-6694">
                      {ws.subRegions.length}
                    </span>
                  </div>
                  {ws.subRegions.length > 0 && <div className="flex flex-wrap gap-1 pt-1" data-id="element-6695">
                      {ws.subRegions.map(r => <span key={r} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700" data-id="element-6696">
                          <MapPin className="h-3 w-3" data-id="element-6697" />
                          {r}
                        </span>)}
                    </div>}
                </div>
              </div>)}
          </div>
        </div>}

      {/* Create Team Modal */}
      {showCreateTeamModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6698">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-6699">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => {
          setShowCreateTeamModal(false);
          resetCreateTeamForm();
        }} data-id="element-6700" />

            <div className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all sm:w-full sm:max-w-lg" data-id="element-6701">
              <div className="px-4 py-5 sm:p-6" data-id="element-6702">
                <div className="flex items-center justify-between mb-5" data-id="element-6703">
                  <h3 className="text-lg font-medium leading-6 text-gray-900" data-id="element-6704">
                    Create New Team
                  </h3>
                  <button onClick={() => {
                setShowCreateTeamModal(false);
                resetCreateTeamForm();
              }} className="text-gray-400 hover:text-gray-500" data-id="element-6705">
                    <X className="h-6 w-6" data-id="element-6706" />
                  </button>
                </div>

                <form onSubmit={handleSubmitCreateTeam} className="space-y-4" data-id="element-6707">
                  <div data-id="element-6708">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-6709">
                      Team Name <span className="text-red-500" data-id="element-6710">*</span>
                    </label>
                    <input type="text" required placeholder="e.g. Marketing, Global, North America" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newTeamName} onChange={e => setNewTeamName(e.target.value)} autoFocus data-id="element-6711" />
                  </div>

                  <div data-id="element-6712">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-6713">
                      Regions (Optional)
                    </label>
                    <div className="mt-1 flex gap-2" data-id="element-6714">
                      <input type="text" placeholder="e.g. New York, London" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newRegionInput} onChange={e => setNewRegionInput(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddRegion(e);
                    }
                  }} data-id="element-6715" />
                      <button type="button" onClick={handleAddRegion} disabled={!newRegionInput.trim()} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50" data-id="element-6716">
                        Add
                      </button>
                    </div>

                    {newTeamRegions.length > 0 && <div className="mt-3 space-y-2 max-h-32 overflow-y-auto" data-id="element-6717">
                        {newTeamRegions.map(region => <div key={region} className="flex items-center justify-between p-2 bg-gray-50 rounded-md border border-gray-100" data-id="element-6718">
                            <span className="text-sm font-medium text-gray-700" data-id="element-6719">
                              {region}
                            </span>
                            <button type="button" onClick={() => handleRemoveRegion(region)} className="text-gray-400 hover:text-red-500" data-id="element-6720">
                              <X className="h-4 w-4" data-id="element-6721" />
                            </button>
                          </div>)}
                      </div>}
                  </div>

                  <div data-id="element-6722">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-6723">
                      Logo URL (Optional)
                    </label>
                    <input type="url" placeholder="https://example.com/logo.png" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newTeamLogoUrl} onChange={e => setNewTeamLogoUrl(e.target.value)} data-id="element-6724" />
                  </div>

                  <div data-id="element-6725">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-6726">
                      Primary Color (Optional)
                    </label>
                    <div className="mt-1 flex gap-2 items-center" data-id="element-6727">
                      <input type="color" className="h-8 w-8 rounded border border-gray-300 cursor-pointer p-0" value={newTeamPrimaryColor} onChange={e => setNewTeamPrimaryColor(e.target.value)} data-id="element-6728" />
                      <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm uppercase" value={newTeamPrimaryColor} onChange={e => setNewTeamPrimaryColor(e.target.value)} pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$" placeholder="#2563eb" data-id="element-6729" />
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-3" data-id="element-6730">
                    <button type="button" onClick={() => {
                  setShowCreateTeamModal(false);
                  resetCreateTeamForm();
                }} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" data-id="element-6731">
                      Cancel
                    </button>
                    <button type="submit" disabled={isCreatingTeam || !newTeamName.trim()} className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50" data-id="element-6732">
                      {isCreatingTeam ? <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" data-id="element-6733" />
                          Creating...
                        </> : 'Create Team'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>}

      <ConfirmModal isOpen={!!userToDelete} title="Delete User" message={`Delete ${userToDelete?.User_Name}?`} confirmLabel="Delete" variant="danger" onCancel={() => setUserToDelete(null)} onConfirm={confirmDelete} data-id="element-6734" />
    </div>;
}